import { useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, ChevronDown, Eraser } from "lucide-react";
import { groupName, groupTint, groupsOf } from "@/lib/leadGroups";

/** Grouped single-select (PRD v2.0 §5.2).
 *  Group headers are visually distinct, not selectable and not focusable;
 *  search matches option labels only; keyboard navigation skips headers. */
export default function GroupedSelect({
  value, onChange, options = [], placeholder = "Select…",
  testid = "grouped-select", disabled = false, allowClear = true,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [cursor, setCursor] = useState(0);
  const listRef = useRef(null);

  const groups = useMemo(() => groupsOf(options), [options]);
  const filtered = useMemo(() => {
    const t = term.trim().toLowerCase();
    return options.filter((o) => !t || (o.label || o.value || "").toLowerCase().includes(t));
  }, [options, term]);

  const sections = useMemo(() => {
    if (!groups.length) return [{ key: null, items: filtered }];
    return groups
      .map((g) => ({ key: g, items: filtered.filter((o) => (o.ownership_group || o.group) === g) }))
      .filter((s) => s.items.length > 0);
  }, [groups, filtered]);

  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  const selected = options.find((o) => o.value === value);

  const pick = (v) => { onChange(v); setOpen(false); setTerm(""); };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => {
        const next = e.key === "ArrowDown" ? c + 1 : c - 1;
        return Math.max(0, Math.min(flat.length - 1, next));
      });
    } else if (e.key === "Enter" && flat[cursor]) {
      e.preventDefault();
      pick(flat[cursor].value);
    }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setTerm(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button" variant="outline" disabled={disabled}
          className={`h-8 justify-between font-normal text-xs w-full ${className}`}
          data-testid={testid}
        >
          <span className="flex items-center gap-1.5 truncate">
            {selected?.color && (
              <span className="inline-block w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: selected.color }} />
            )}
            <span className={selected ? "text-slate-900 truncate" : "text-slate-400"}>
              {selected ? (selected.label || selected.value) : placeholder}
            </span>
          </span>
          <ChevronDown className="w-3 h-3 text-slate-400 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[280px]" onKeyDown={onKeyDown}>
        <div className="p-2 border-b border-slate-100">
          <Input
            autoFocus value={term} onChange={(e) => { setTerm(e.target.value); setCursor(0); }}
            placeholder="Search…" className="h-7 text-xs"
            data-testid={`${testid}-search`}
          />
        </div>
        <div ref={listRef} className="max-h-64 overflow-y-auto py-1" data-testid={`${testid}-list`}>
          {allowClear && (
            <button
              type="button" onClick={() => pick("")}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-50"
              data-testid={`${testid}-clear`}
            >
              <Eraser className="w-3 h-3" /> -- Clear --
            </button>
          )}
          {sections.length === 0 && (
            <div className="px-3 py-6 text-center text-xs text-slate-400">No matches</div>
          )}
          {sections.map((s) => (
            <div key={s.key || "flat"}>
              {s.key && (
                <div
                  role="presentation" aria-hidden="true" aria-disabled="true" tabIndex={-1}
                  className={`px-3 py-1 text-[10px] font-semibold uppercase tracking-wider ${groupTint(s.key)}`}
                  data-testid={`${testid}-group-${s.key}`}
                >
                  {groupName(s.key)}
                </div>
              )}
              {s.items.map((o) => {
                const idx = flat.indexOf(o);
                return (
                  <button
                    key={o.value} type="button" onClick={() => pick(o.value)}
                    onMouseEnter={() => setCursor(idx)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs text-left
                      ${idx === cursor ? "bg-indigo-50" : "hover:bg-slate-50"}`}
                    data-testid={`${testid}-option-${o.value}`}
                  >
                    <span className="flex items-center gap-2 truncate">
                      {o.color && (
                        <span className="inline-block w-2 h-2 rounded-full shrink-0"
                              style={{ backgroundColor: o.color }} />
                      )}
                      <span className="truncate text-slate-700">{o.label || o.value}</span>
                    </span>
                    {value === o.value && <Check className="w-3.5 h-3.5 text-indigo-600 shrink-0" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
